import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { useLocation, useHistory, useRouteMatch } from 'react-router';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';
import { reduxForm, formValueSelector } from 'redux-form';

import type { RootState } from 'af-reducers';

import { TOOL_UPDATE } from 'af-constants/reduxForms';
import CLIENT from 'af-constants/routes/client';

import Breadcrumbs from 'af-components/Breadcrumbs';
import LoadingIndicator from 'af-components/LoadingIndicator';

import * as ToolActions from 'af-actions/tool';

import Form from '../Shared/Form';
import FormModel from '../Shared/formModel';
import { validate } from '../Shared/validation';

interface PathParams {
	id: string;
}

type Props = ConnectedProps<typeof connector> & InjectedFormProps<FormModel>;

const Edit = (props: Props) => {
	const {
		findById,
		initialize,
		update,
		companyName,
		status,
		toolState,
	} = props;

	const match = useRouteMatch<PathParams>();
	const history = useHistory();
	const location = useLocation<{ orgAlias: string; }>();

	const { orgAlias } = location.state;
	const id = match.params.id;

	const [loading, setLoading] = React.useState(true);

	const loadStuff = React.useCallback(async () => {
		const item = await findById(+id);
		const form = FormModel.fromVM(item);
		initialize(form);
		setLoading(false);
		// We don't want initialize to cause a rerun
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [findById, id]);

	React.useEffect(() => {
		loadStuff();
	}, [loadStuff]);

	const updateItem = React.useCallback(async (form: FormModel) => {
		await update(form.id, FormModel.toRequestModel(form));
		history.push(CLIENT.COMPANY.EQUIPMENT.TOOL.LIST(orgAlias, companyName));
	}, [companyName, history, orgAlias, update]);

	return (
		<div className="form-segment">
			<Breadcrumbs
				items={[
					{ label: 'Equipment', url: CLIENT.COMPANY.EQUIPMENT.TOOL.LIST(orgAlias, companyName) },
					{ label: 'Small Tool' },
				]}
			/>
			{loading ? (
				<div className="form-box form-box--loading-only">
					<LoadingIndicator color="orange" size="big" />
				</div>
			) : (
				<Form
					{...props}
					onSubmit={updateItem}
					state={toolState}
					status={status}
				/>
			)}
		</div>
	);
};

const formSelector = formValueSelector(TOOL_UPDATE);

interface FormActionWrapper {
	selector: <T extends keyof FormModel>(fieldName: T) => FormModel[T];
}
const formActionWrapper: Partial<FormActionWrapper> = {
	selector: undefined,
};

function mapStateToProps(state: RootState) {
	const { companyData } = state.user;
	if (!companyData) {
		throw new Error('User not logged in');
	}

	formActionWrapper.selector = (fieldName: keyof FormModel) => formSelector(state, fieldName);

	const status = formSelector(state, 'status');
	const toolState = formSelector(state, 'state');

	return {
		formActionWrapper: formActionWrapper as FormActionWrapper, // selector is no longer undefined
		companyName: companyData.name,
		status,
		toolState,
	};
}

function mapDispatchToProps() {
	return {
		findById: ToolActions.findById,
		update: ToolActions.update,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose(
	connector,
	reduxForm({
		form: TOOL_UPDATE,
		validate,
		enableReinitialize: true,
		keepDirtyOnReinitialize: true,
	})
);

export default enhance(Edit);
